import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {catchError, debounceTime, map, merge, Observable, of, startWith, Subject, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TireService} from "../../../shared/services/tire.service";
import {AutocompleteComponent} from "../../../shared/components/autocomplete/autocomplete.component";
import {MatCheckbox} from "@angular/material/checkbox";
import {TranslatePipe} from "@ngx-translate/core";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {TireSpecResponse} from "../../../shared/models/tire/TireSpecResponse";
import {MatFormField, MatLabel} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";

interface TruckTireSpecData {
  truckSpecId: number;
  tireSpecIds: number[];
}

@Component({
  selector: 'app-add-new-fleet-modal',
  standalone: true,
  templateUrl: 'add-tire-specification-modal.component.html',
  styleUrls: ['add-tire-specification-modal.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSortModule,
    AutocompleteComponent,
    MatCell,
    MatCellDef,
    MatCheckbox,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatPaginator,
    MatProgressSpinner,
    MatRow,
    MatRowDef,
    MatTable,
    TranslatePipe,
    MatHeaderCellDef,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption
  ]
})

export class AddTireSpecificationModalComponent implements OnInit, AfterViewInit {
  readonly dialogRef = inject(MatDialogRef<AddTireSpecificationModalComponent>);
  private TireService = inject(TireService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: TruckTireSpecData
  ) {}

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['select', 'seat', 'manufacturer', 'specificationFull'];
  dataSource = new MatTableDataSource<TireSpecResponse>();

  private allTiresSpec: TireSpecResponse[] = [];
  private filtersChanged$ = new Subject<void>();

  allSeats: number[] = [];
  allManufacturers: string[] = [];

  selectedSeat?: number;
  selectedManufacturer?: string;

  filteredSeats: number[] = [];
  filteredManufacturers: string[] = [];

  filteredTireSpec$!: Observable<TireSpecResponse[]>;

  filterCtrl = new FormControl<string>('');
  isLoading = false;
  isTableLoading = true;

  isSubmitting = false;

  selection: TireSpecResponse[] = [];

  pageSize = 10;
  pageSizeOptions = [10, 15, 30];

  ngOnInit() {
    this.isTableLoading = true;
    this.getAllTireSpec();
  }

  getAllTireSpec() {
    this.TireService.getAllTireSpecs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const existingIds = this.data.tireSpecIds ?? [];
          const filteredData = data.filter((tire) => !existingIds.includes(tire.id));

          this.allTiresSpec = filteredData;
          this.dataSource.data = filteredData;

          this.allSeats = Array.from(new Set(filteredData.map((x) => x.seat))).sort((a, b) => a - b);
          this.allManufacturers = Array.from(new Set(filteredData.map((x) => x.manufacturer))).sort();

          this.filteredSeats = [...this.allSeats];
          this.filteredManufacturers = [...this.allManufacturers];

          this.setupAutocomplete();

          this.dataSource.filterPredicate = (data, filter) => {
            const f = JSON.parse(filter || '{}');
            const seatMatch = f.seat === undefined ? true : data.seat === f.seat;
            const manuMatch = f.manufacturer === undefined ? true : data.manufacturer === f.manufacturer;
            const search = f.search === undefined ? undefined : String(f.search).toLowerCase();
            const searchMatch = search ? data.specificationFull.toLowerCase().includes(search) : true;
            return seatMatch && manuMatch && searchMatch;
          };
        },
        error: () => {
          this.isTableLoading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isTableLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private getBaseFilteredTireSpec(search?: string): TireSpecResponse[] {
    return this.allTiresSpec.filter(
      (tireSpec) =>
        (this.selectedSeat === undefined || tireSpec.seat === this.selectedSeat) &&
        (this.selectedManufacturer === undefined || tireSpec.manufacturer === this.selectedManufacturer) &&
        (!search || tireSpec.specificationFull.toLowerCase().includes(search))
    );
  }

  private setupAutocomplete() {
    this.filteredTireSpec$ = merge(this.filterCtrl.valueChanges, this.filtersChanged$).pipe(
      startWith(null),
      debounceTime(300),
      map(() => {
        this.isLoading = true;
        this.cdr.detectChanges();
        return (this.filterCtrl.value ?? '').toLowerCase();
      }),
      map((search) => {
        const results = this.getBaseFilteredTireSpec(search);
        this.recomputeAvailableFilters(search);
        this.applySearchFilter(search);
        this.isLoading = false;
        this.cdr.detectChanges();
        return results.slice(0, 10);
      }),
      catchError(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
        return of([]);
      })
    );
  }

  private recomputeAvailableFilters(search?: string) {
    const searchLower = (search ?? '').toLowerCase();

    const baseForSeats = this.allTiresSpec.filter(
      (tireSpec) =>
        (this.selectedManufacturer === undefined || tireSpec.manufacturer === this.selectedManufacturer) &&
        (!searchLower || tireSpec.specificationFull.toLowerCase().includes(searchLower))
    );
    this.filteredSeats = Array.from(new Set(baseForSeats.map((tireSpec) => tireSpec.seat))).sort((a, b) => a - b);

    const baseForManufacturers = this.allTiresSpec.filter(
      (tireSpec) =>
        (this.selectedSeat === undefined || tireSpec.seat === this.selectedSeat) &&
        (!searchLower || tireSpec.specificationFull.toLowerCase().includes(searchLower))
    );
    this.filteredManufacturers = Array.from(new Set(baseForManufacturers.map((tireSpec) => tireSpec.manufacturer))).sort();

    if (this.selectedSeat !== undefined && !this.filteredSeats.includes(this.selectedSeat)) {
      this.selectedSeat = undefined;
    }
    if (this.selectedManufacturer !== undefined && !this.filteredManufacturers.includes(this.selectedManufacturer)) {
      this.selectedManufacturer = undefined;
    }
  }

  protected onElementSelected(tireSpec: TireSpecResponse) {
    this.filterCtrl.setValue(tireSpec.specificationFull, { emitEvent: false });
    this.applySearchFilter(tireSpec.specificationFull.toLowerCase());
    this.isLoading = false;
  }

  private applySearchFilter(value: string) {
    const filter = {
      seat: this.selectedSeat,
      manufacturer: this.selectedManufacturer,
      search: value || undefined
    };

    this.dataSource.filter = JSON.stringify(filter);
    this.dataSource.paginator?.firstPage();
    this.refreshTable();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.cdr.detectChanges();
  }


  private applyGlobalFilter() {
    const search = (this.filterCtrl.value as string | null)?.toLowerCase() || undefined;
    const filter = {
      seat: this.selectedSeat,
      manufacturer: this.selectedManufacturer,
      search
    };

    this.dataSource.filter = JSON.stringify(filter);
    this.dataSource.paginator?.firstPage();
    this.refreshTable();
  }

  applySeatFilter() {
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  applyManufacturerFilter() {
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  clearSeat() {
    this.selectedSeat = undefined;
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  clearManufacturer() {
    this.selectedManufacturer = undefined;
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  toggleSelection(row: TireSpecResponse) {
    const index = this.selection.indexOf(row);
    index >= 0 ? this.selection.splice(index, 1) : this.selection.push(row);
  }

  private refreshTable() {
    this.dataSource.filter = this.dataSource.filter;
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  addTireSpecifications() {
    if (this.selection.length === 0 || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    this.dialogRef.close({
      truckSpecId: this.data.truckSpecId,
      specs: this.selection as TireSpecResponse[]
    });
  }

  closeModal() {
    this.dialogRef.close();
  }
}
